import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../utils/api";
import { showToast } from "../toastService";
import "./css/ContractorCard.css";

export default function ContractorCard({ contractor, lang = "en", onFavoriteUpdate }) {
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(contractor.is_favorite || false);
  const [updatingFavorite, setUpdatingFavorite] = useState(false);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);

  const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
  const VERSION = process.env.REACT_APP_VERSION;

  // Translations
  const t = {
    en: {
      description: "Description",
      categories: "Categories",
      specialisations: "Specialisations",
      viewDetails: "View Details",
      allCategories: "All Categories",
      close: "Close",
      yearsExperience: "Yrs",
      monthsExperience: "Mths",
    },
    ar: {
      description: "الوصف",
      categories: "الفئات",
      specialisations: "التخصصات",
      viewDetails: "عرض التفاصيل",
      allCategories: "جميع الفئات",
      close: "إغلاق",
      yearsExperience: "سنة",
      monthsExperience: "شهر",
    },
  }[lang];

  // Calculate years and months of experience
  const calculateExperience = () => {
    if (!contractor.company_start_date) return null;
    const startDate = new Date(contractor.company_start_date);
    const currentDate = new Date();
    const diffTime = Math.abs(currentDate - startDate);
    const diffYears = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 365));
    const diffMonths = Math.floor(
      (diffTime % (1000 * 60 * 60 * 24 * 365)) / (1000 * 60 * 60 * 24 * 30)
    );
    return { years: diffYears, months: diffMonths };
  };

  const experience = calculateExperience();

  // Get formatted categories (subcategory - category format)
  const getFormattedCategories = () => {
    if (!contractor.sub_categories || contractor.sub_categories.length === 0) return [];
    return contractor.sub_categories.map((sc) => {
      if (typeof sc === "object" && sc.category_id && typeof sc.category_id === "object") {
        return `${sc.name} - ${sc.category_id.name}`;
      }
      return typeof sc === "object" ? sc.name : sc;
    });
  };

  const formattedCategories = getFormattedCategories();
  const formattedSpecialisations = (contractor.specialisations || []).map((s) =>
    typeof s === "object" && s.name ? s.name : (typeof s === "string" ? s : "")
  ).filter(Boolean);
  const showMoreCount = formattedCategories.length > 1 ? formattedCategories.length - 1 : 0;

  // Handle favorite toggle
  const handleFavoriteToggle = async (e) => {
    e.stopPropagation();
    if (updatingFavorite || !contractor._id) return;

    try {
      setUpdatingFavorite(true);
      const newFavoriteStatus = !isFavorite;
      const contractorId = String(contractor._id);

      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/contractor/add-update-favorites/`,
        "POST",
        JSON.stringify({
          id: contractorId,
          is_favorite: newFavoriteStatus,
        }),
        {
          "Content-Type": "application/json",
        }
      );

      if (data && data.response_code === 200) {
        setIsFavorite(newFavoriteStatus);
        if (onFavoriteUpdate) {
          onFavoriteUpdate(contractorId, newFavoriteStatus);
        }
      }
    } catch (err) {
      console.error("Error updating favorite:", err);
      showToast(err.message || "Failed to update favorite", "error");
    } finally {
      setUpdatingFavorite(false);
    }
  };

  // Render stars
  const renderStars = () => {
    const rating = contractor.average_rating || 0;
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="contractor-rating">
        {[...Array(fullStars)].map((_, i) => (
          <i key={i} className="bx bxs-star" style={{ color: "#f59e0b" }}></i>
        ))}
        {hasHalfStar && (
          <i className="bx bxs-star-half" style={{ color: "#f59e0b" }}></i>
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <i key={i} className="bx bx-star" style={{ color: "#d1d5db" }}></i>
        ))}
        <span className="rating-text" style={{ color: "#f59e0b" }}>
          {rating.toFixed(1)} / 5.0
        </span>
      </div>
    );
  };

  return (
    <div className="contractor-card" dir={lang === "ar" ? "rtl" : "ltr"}>
      <div className="contractor-card-header">
        <div className="contractor-logo-container">
          {contractor.profile_image && contractor.profile_image.url ? (
            <img
              src={contractor.profile_image.url}
              alt={contractor.name || "Contractor"}
              className="contractor-logo"
            />
          ) : (
            <div className="contractor-logo-placeholder">
              <i className="bx bx-building"></i>
            </div>
          )}
        </div>
        <div className="contractor-info-container">
          <div className="contractor-name-container">
            <h3 className="contractor-name">{contractor.name || "N/A"}</h3>
          </div>
          {renderStars()}
          {experience && (
            <div className="contractor-experience">
              <i className="bx bx-calendar"></i>
              <span>
                {experience.years} {t.yearsExperience}{" "}
                {experience.months} {t.monthsExperience}
              </span>
            </div>
          )}
        </div>
        <button
          className={`contractor-favorite-btn ${isFavorite ? "active" : ""}`}
          onClick={handleFavoriteToggle}
          disabled={updatingFavorite}
          title={isFavorite ? "Remove from favorites" : "Add to favorites"}
        >
          {updatingFavorite ? (
            <div className="button-spinner-small"></div>
          ) : (
            <i
              className={isFavorite ? "bx bxs-heart" : "bx bx-heart"}
            ></i>
          )}
        </button>
      </div>

      <div className="contractor-card-body">
        {/* Description */}
        {contractor.description && (
          <div className="contractor-description-section">
            <h4 className="contractor-section-title">{t.description}</h4>
            <p className="contractor-description-text">{contractor.description}</p>
          </div>
        )}

        {/* Categories */}
        {formattedCategories.length > 0 && (
          <div className="contractor-categories-section">
            <div className="contractor-categories-list">
              {formattedCategories.slice(0, 1).map((cat, index) => (
                <span key={index} className="contractor-category-chip">
                  {cat}
                </span>
              ))}
              {showMoreCount > 0 && (
                <span
                  className="contractor-category-chip more"
                  onClick={() => setShowCategoriesModal(true)}
                  style={{ cursor: "pointer" }}
                >
                  +{showMoreCount} More
                </span>
              )}
            </div>
          </div>
        )}

        {/* Specialisations */}
        {formattedSpecialisations.length > 0 && (
          <div className="contractor-categories-section">
            <h4 className="contractor-section-title">{t.specialisations}</h4>
            <div className="contractor-categories-list">
              {formattedSpecialisations.slice(0, 3).map((spec, index) => (
                <span key={index} className="contractor-category-chip">
                  {spec}
                </span>
              ))}
              {formattedSpecialisations.length > 3 && (
                <span className="contractor-category-chip">
                  +{formattedSpecialisations.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="contractor-card-footer">
        <button
          className="contractor-view-details-link"
          onClick={() => navigate(`/contractor/${contractor._id}`)}
        >
          {t.viewDetails} &gt;
        </button>
      </div>

      {/* Categories Modal */}
      {showCategoriesModal && (
        <div
          className="categories-modal-overlay"
          onClick={() => setShowCategoriesModal(false)}
        >
          <div
            className="categories-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="categories-modal-header">
              <h3>{t.allCategories}</h3>
              <button
                className="categories-modal-close"
                onClick={() => setShowCategoriesModal(false)}
              >
                <i className="bx bx-x"></i>
              </button>
            </div>
            <div className="categories-modal-body">
              {formattedCategories.map((cat, index) => (
                <span key={index} className="contractor-category-chip">
                  {cat}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

